import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Wrench, MoreVertical, Pencil, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", cnpj: "", contact: "", phone: "", email: "", endereco: "", especialidade: "" };

export default function Workshops() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = () => api.get("/workshops").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (w) => {
    setEditing(w);
    setForm({
      name: w.name || "", cnpj: w.cnpj || "", contact: w.contact || "",
      phone: w.phone || "", email: w.email || "",
      endereco: w.endereco || "", especialidade: w.especialidade || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) return toast.error("Nome é obrigatório");
    try {
      if (editing) {
        await api.put(`/workshops/${editing.id}`, { ...form, active: editing.active !== false });
        toast.success("Oficina atualizada");
      } else {
        await api.post("/workshops", form);
        toast.success("Oficina cadastrada");
      }
      setOpen(false); setForm(EMPTY); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleActive = async (w) => {
    try {
      await api.patch(`/workshops/${w.id}`, { active: w.active === false });
      toast.success(w.active === false ? "Oficina ativada" : "Oficina inativada");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/workshops/${pendingDelete.id}`);
      toast.success("Oficina excluída"); setPendingDelete(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="workshops-page">
      <PageHeader title="Oficinas" subtitle="Rede de parceiros para orçamentos e execução"
        actions={
          <Button className="gap-2" data-testid="new-workshop-btn" onClick={openNew}>
            <Plus className="w-4 h-4" />Nova Oficina
          </Button>
        } />

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar Oficina" : "Nova Oficina"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nome*</Label><Input data-testid="ws-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>CNPJ</Label><Input data-testid="ws-cnpj" value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} placeholder="00.000.000/0000-00" /></div>
            <div><Label>Especialidade</Label><Input data-testid="ws-specialty" value={form.especialidade} onChange={(e) => setForm({ ...form, especialidade: e.target.value })} placeholder="Motor, elétrica, funilaria..." /></div>
            <div><Label>Contato</Label><Input data-testid="ws-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} /></div>
            <div><Label>Telefone</Label><Input data-testid="ws-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="col-span-2"><Label>E-mail</Label><Input type="email" data-testid="ws-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="col-span-2"><Label>Endereço</Label><Input data-testid="ws-address" value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} placeholder="Rua, número, bairro, cidade/UF" /></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-workshop">Salvar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>CNPJ</TableHead>
              <TableHead>Especialidade</TableHead>
              <TableHead>Contato</TableHead>
              <TableHead>Telefone</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right w-16">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.map((w) => {
              const inactive = w.active === false;
              return (
                <TableRow key={w.id} data-testid={`ws-row-${w.id}`} className={inactive ? "opacity-60" : ""}>
                  <TableCell className="font-semibold flex items-center gap-2"><Wrench className="w-4 h-4 text-primary" />{w.name}</TableCell>
                  <TableCell className="font-mono text-xs">{w.cnpj || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{w.especialidade || "—"}</TableCell>
                  <TableCell>{w.contact || "—"}</TableCell>
                  <TableCell className="font-mono">{w.phone || "—"}</TableCell>
                  <TableCell className="text-xs">{w.email || "—"}</TableCell>
                  <TableCell>
                    {inactive
                      ? <Badge variant="outline" className="text-[10px] text-muted-foreground">Inativo</Badge>
                      : <Badge variant="outline" className="text-[10px] bg-primary/10 border-primary/30 text-primary">Ativo</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="w-7 h-7" data-testid={`ws-menu-${w.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        <DropdownMenuItem onClick={() => openEdit(w)} data-testid={`ws-edit-${w.id}`}>
                          <Pencil className="w-4 h-4 mr-2" /> Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => toggleActive(w)} data-testid={`ws-toggle-${w.id}`}>
                          <Power className="w-4 h-4 mr-2" /> {inactive ? "Ativar" : "Inativar"}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setPendingDelete(w)} className="text-destructive focus:text-destructive" data-testid={`ws-delete-${w.id}`}>
                          <Trash2 className="w-4 h-4 mr-2" /> Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir oficina?</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a oficina <b>{pendingDelete?.name}</b>? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ws-cancel-delete">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="ws-confirm-delete">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
